import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Report } from '../types';
import {
    QueueEntry, canFlush, classifySyncError, markSyncFailure,
    istZuAlt, istZuAltBlockiert, istZuAltGemeldet, istVerwaisterSync,
    markTooOld, markTooOldAnnounced, queueEntryProjectLabel,
    releaseOrphanedSync, darfAbschlussAnwenden, darfAbschlussLoeschen,
    starteFlush, erzeugeEintragsId, zurueckInDieWarteschlange,
    lokalerStandZaehlt, istAbgewiesen, darfAngehaltenWerden, abgewiesenMeldung, abgewiesenErneutVersuchen,
    type AbschlussKennung, type FlushStart, type SyncFailureKind,
} from '../services/offlineQueueLogic';
import { istEigenerNetzFehler } from '../services/netzFehler';
import {
    getQueueEntry, listQueueEntries, mutateQueueEntry,
    putLatestGebuendelt, schreibeWartendeJetzt, lokaleSicherungOffen, lokalerStandUngesichert,
} from '../services/offlineQueue';
// Nur die reine Sperr-LOGIK (kein Netz, kein Supabase, kein App-Zustand). Wer
// die Sperre tatsaechlich abfragt, entscheidet der Aufrufer und reicht es als
// `canUpload`-Callback herein: importierte dieser Hook den Sperr-Service und
// den Projektzustand direkt, entstuende eine Schleife zwischen App-Zustand und
// Speicherweg — und ein Hook, der ohne Supabase nicht mehr zu betrachten ist.
import { QueueUploadDecision, shouldAnnounceUploadBlock } from '../services/projectLockLogic';
import { isProjectSaveConflict, type SaveAnchorOptions } from '../services/projectVersionAnchor';
import { markiereBuildVeraltet } from '../services/versionGate';
import { bergeEntwurfAufServerstand, mergeReports, salvageForeignLogEntries, type MergeConflict } from '../services/reportMerge';
import type { TicketNummerWechsel } from '../services/inspectionItemStatus';

// Erweiterter Speicherstatus: die ersten vier Werte entsprechen dem bisherigen
// Verhalten (Sidebar), die übrigen kommen aus der Offline-Queue.
export type SaveStatus =
    | 'idle' | 'saving' | 'saved' | 'error'
    | 'queued' | 'offline' | 'storage-error' | 'auth-required';

/** Was beim Hochladen mit einem überholten Stand geschehen ist. */
export interface MergeReport {
    /** 'merge' = mit Ausgangsstand zusammengeführt, 'salvage' = Alteintrag, nur Protokoll gerettet. */
    mode: 'merge' | 'salvage';
    conflicts: MergeConflict[];
    keptForeignLogEntries: number;
    removedLogEntries: number;
    /** Neue Mängel dieses Eintrags, die wegen belegter Nummer eine neue bekamen. */
    ticketNummernWechsel: TicketNummerWechsel[];
}

/**
 * Welcher STAND vom Altersriegel angehalten wurde — die Identität eines
 * Hinweises, nicht die eines Projekts.
 *
 * Alles, was der Anwender an einem gezeigten Balken tut (quittieren,
 * verwerfen), greift ausschliesslich über diese Kennung. Der Grund ist der
 * zeitliche Abstand: zwischen Meldung und Klick können Minuten liegen, in
 * denen derselbe Schlüssel [userId, projectId] längst einen anderen Eintrag
 * trägt — frisch gespeichert, erneut veraltet, oder der eines anderen
 * Benutzers, der sich inzwischen angemeldet hat. Ein Klick auf einen alten
 * Balken würde dann einen Stand stilllegen oder löschen, den niemand je
 * gesehen hat.
 *
 * BEWUSST ohne `entryId`: Bestandsdaten aus Builds vor dem 31.08.2026 tragen
 * keine, und `normalizeQueueEntry` vergibt bei JEDEM Lesen eine neue,
 * flüchtige — ein Vergleich darauf schlüge bei genau den ältesten Einträgen
 * immer fehl, also bei denen, um die es hier geht. `rev` und `updatedAt`
 * reichen: `coalesceReportSave` zählt `rev` hoch UND setzt `updatedAt` neu,
 * jeder neue Stand ist damit an beidem zu erkennen.
 */
export interface ZuAltKennung {
    userId: string;
    projectId: string;
    rev: number;
    /** `entry.updatedAt ?? entry.queuedAt` — dieselbe Quelle wie `queueEntryAgeMs`. */
    updatedAt: string;
}

interface Options {
    userId: string | null;
    /**
     * Führt den eigentlichen Cloud-Save aus.
     *
     * `options` trägt den ANKER: den Serverstand, auf dem dieser Eintrag
     * aufsetzt. Der Aufrufer muss ihn durchreichen — ohne ihn schreibt der
     * Eintrag wieder bedingungslos über alles, was inzwischen passiert ist,
     * und genau daran sind am 31.08.2026 19 Buchungszeilen verloren gegangen.
     */
    saveReport: (entry: QueueEntry, options?: SaveAnchorOptions) => Promise<any | null>;
    /**
     * Holt den aktuellen Serverstand eines Projekts — gebraucht wird er NUR,
     * wenn der Anker nicht trifft. Fehlt dieser Zugang, kann ein überholter
     * Eintrag nicht zusammengeführt werden; er bleibt dann liegen und wird
     * später erneut versucht, statt den fremden Stand zu überschreiben.
     */
    loadServerReport?: (projectId: string) => Promise<{ report: Report; updatedAt: string | null } | null>;
    /** Meldung, wenn ein Eintrag zusammengeführt werden musste. */
    onMerged?: (projectId: string, info: MergeReport) => void;
    onStatus: (status: SaveStatus) => void;
    /** Nach erfolgreichem Flush: Server-Zeile + gespeicherter Report für den State-Merge. */
    onFlushed: (projectId: string, row: any, savedReport: Report) => void;
    /**
     * Torwächter VOR jedem Hochladen.
     *
     * Ohne ihn ist die Queue der einzige Schreibweg, den niemand auslöst und
     * den niemand sieht: sie flusht bei `online`, beim Sichtbarwerden des Tabs
     * und alle 45 s. Wer vier Minuten offline war, hat seine Projektsperre
     * verloren — sein alter Stand darf dann nicht ungefragt über die Arbeit
     * dessen laufen, der inzwischen übernommen hat.
     *
     * Optional und fail-open: fehlt der Torwächter oder wirft er, wird
     * hochgeladen. Ein Sperrfehler darf niemals Arbeit festhalten.
     */
    canUpload?: (projectId: string) => Promise<QueueUploadDecision>;
    /**
     * Einseitige Meldung an den Benutzer, wenn ein Eintrag liegen bleibt —
     * wegen einer fremden Sperre (geht später von selbst raus) oder weil der
     * Altersriegel ihn angehalten hat (geht NICHT mehr raus, siehe
     * MAX_QUEUE_AGE_MS in offlineQueueLogic.ts; der Text sagt das). KEINE
     * Rückfrage — es gibt in beiden Fällen nichts zu entscheiden.
     *
     * `grund` trennt genau diese beiden Fälle, weil sie verschieden enden:
     * 'sperre' löst sich von selbst, sobald das Projekt frei ist; 'zu-alt'
     * löst sich nie, und nur dafür darf der Aufrufer ein Verwerfen anbieten
     * (`discardTooOldEntry`). Ein Verwerfen-Knopf an einer Sperrabsage
     * löschte Arbeit, die gleich noch hochgeht.
     *
     * ACHTUNG, Pflicht des Aufrufers bei 'zu-alt': Es können MEHRERE alte
     * Einträge hintereinander gemeldet werden (flushAll geht die ganze
     * Warteschlange durch). Wer sie in eine einzige Variable schreibt, zeigt
     * nur den letzten — und die übrigen hat nie jemand gesehen. Deshalb
     * sammelt App.tsx sie und quittiert jeden einzeln mit
     * `acknowledgeTooOld(kennung)`, wenn er weggeklickt wurde; erst diese
     * Quittung macht ihn dauerhaft still.
     *
     * `kennung` liegt bei 'zu-alt' IMMER an und beschreibt genau den Stand,
     * über den hier gesprochen wird — nicht das Projekt. Sie ist der einzige
     * zulässige Schlüssel für Quittieren und Verwerfen: zwischen Meldung und
     * Klick kann derselbe Eintrag längst ein anderer sein (neu gespeichert,
     * erneut veraltet), und dann gehört der alte Balken zu einem Stand, den
     * es nicht mehr gibt.
     *
     * 'abgewiesen': die Datenbank hat den Inhalt abgelehnt (offlineQueueLogic,
     * `istDatenbankAbsage`). Geht mit der nächsten Änderung am Projekt oder
     * beim nächsten Start erneut raus; zu verwerfen gibt es nichts, eine
     * Kennung kommt nicht mit. Das nächste gelungene Speichern desselben
     * Projekts darf den Balken wegnehmen.
     */
    onUploadBlocked?: (
        projectId: string,
        message: string,
        grund: 'sperre' | 'zu-alt' | 'abgewiesen',
        kennung?: ZuAltKennung,
    ) => void;
    /**
     * Der Server hat einen Upload mit dem Build-Riegel abgewiesen
     * (`BUILD_VERALTET`) — dieses Bundle darf nicht mehr schreiben.
     *
     * Der Hook hat den Befund da schon selbst vermerkt
     * (`markiereBuildVeraltet`), damit jeder weitere Schreibweg sofort
     * stillsteht. Was daraus auf dem Bildschirm wird — Reload oder
     * Update-Balken — entscheidet der Aufrufer, mit derselben Regel wie das
     * Version-Gate (App.tsx: decideOnOutdated).
     */
    onBuildOutdated?: () => void;
}

/**
 * Womit ein ÜBERHOLTER Eintrag nach dem Zusammenführen geschrieben wird.
 *
 * Mit dem FRISCHEN Anker und `onConflict: 'throw'`: läuft in diesem Moment
 * ein dritter Schreibvorgang, schlägt auch dieser Versuch fehl — und das ist
 * richtig so. Ein Zusammenführen gegen einen Stand, der schon wieder
 * überholt ist, verlöre genau das, was es retten soll.
 *
 * Und OHNE die Zähler der Positionen (`counters: 'keep-server'`): dieser
 * Eintrag hat den Anker verfehlt, sein Stand ist nachweislich älter als der
 * Server. Was er über quantity_used / quantity_billed / status weiß, ist so
 * alt wie der Rest — und genau das hat am 07.09.2026 in 24 Projekten das
 * Protokoll gerettet und im selben Zug den Zähler aus dem alten Stand
 * darüber geschrieben (Protokoll N, quantity_used N-1). Der Server behält
 * seine Werte; der Schreibweg lässt die Schlüssel weg.
 *
 * Reine Funktion, damit sie ohne React und ohne IndexedDB prüfbar ist.
 */
export const staleEntrySaveOptions = (
    serverUpdatedAt: string | null | undefined,
    ohneAusgangsstand = false,
): SaveAnchorOptions => ({
    expectedUpdatedAt: serverUpdatedAt ?? null,
    onConflict: 'throw',
    counters: 'keep-server',
    // Nur die Bergung: die Zusammenführung MIT Ausgangsstand nimmt die
    // Positionen und Notizen des Servers selbst auf und weiß, was der Eintrag
    // entfernt hat — dort bleibt der Abgleich, wie er ist.
    ...(ohneAusgangsstand ? { kindZeilen: 'nur-ergaenzen' as const } : {}),
});

/** Was beim Öffnen aus einem liegenden Eintrag und dem Serverstand wird. */
export interface EntwurfAufServerstand {
    merged: Report;
    /** 'merge' = mit Ausgangsstand zusammengeführt, 'salvage' = Alteintrag geborgen. */
    mode: 'merge' | 'salvage';
    conflicts: MergeConflict[];
    keptForeignLogEntries: number;
    removedLogEntries: number;
    /** Nur bei der Bergung: Positionen und Notizen des Servers, die in den Entwurf kamen. */
    uebernommeneServerZeilen: number;
}

/**
 * Einen liegenden Eintrag als Entwurf auf den Serverstand legen — beim
 * Öffnen eines Projekts und auf dem Rückweg nach einer Sperre (App.tsx).
 *
 * Mit Ausgangsstand: `mergeReports`. Ohne: die Bergung mit den Positionen
 * und Notizen des Servers (`bergeEntwurfAufServerstand`) — nicht nur das
 * Protokoll wie beim Hochladen. Der Grund steht dort: nach dem Öffnen trifft
 * der nächste Speichervorgang den Anker, und die Kindtabellen würden gegen
 * den Entwurf abgeglichen.
 *
 * Ob der Eintrag überhaupt zählt (Alter, Altersriegel), entscheidet vorher
 * `lokalenEntwurfHolen`. Reine Funktion, damit sie ohne React prüfbar ist.
 */
export const entwurfAufServerstand = (queued: QueueEntry, server: Report): EntwurfAufServerstand => {
    if (queued.base) {
        const r = mergeReports(queued.base, queued.report, server);
        return {
            merged: r.merged, mode: 'merge', conflicts: r.conflicts,
            keptForeignLogEntries: r.keptForeignLogEntries, removedLogEntries: r.removedLogEntries,
            uebernommeneServerZeilen: 0,
        };
    }
    const r = bergeEntwurfAufServerstand(queued.report, server);
    return {
        merged: r.merged, mode: 'salvage', conflicts: r.conflicts,
        keptForeignLogEntries: r.keptForeignLogEntries, removedLogEntries: r.removedLogEntries,
        uebernommeneServerZeilen: r.uebernommeneServerZeilen,
    };
};

/**
 * Welche Einträge dieser Durchlauf aus 'syncing' zurückholen darf.
 *
 * Die Regel selbst (`istVerwaisterSync`) kennt nur Zustand und Zeit; die
 * zweite Bedingung kann sie gar nicht kennen: Die IndexedDB gehört ALLEN Tabs
 * gemeinsam, und der eigene, gerade laufende Flush darf sich nicht selbst
 * zurücksetzen — er schriebe sonst gleich darauf seinen Abschluss auf einen
 * Eintrag, den dieser Durchlauf soeben wieder zum Hochladen freigegeben hat.
 * Was ein FREMDER Tab hält, ist von hier aus nicht zu sehen; dafür steht die
 * Fünf-Minuten-Grenze.
 *
 * Reine Funktion, damit dieser Schnitt ohne React prüfbar ist (Muster
 * `staleEntrySaveOptions`).
 */
export const verwaisteSyncsZumFreigeben = (
    entries: QueueEntry[],
    haeltDieserTab: (projectId: string) => boolean,
    nowIso: string,
): QueueEntry[] => entries.filter(e => istVerwaisterSync(e, nowIso) && !haeltDieserTab(e.projectId));

/**
 * Muss die Absage des Altersriegels für diesen Eintrag noch auf den
 * Bildschirm?
 *
 * Zwei Gedächtnisse, und beide werden gebraucht: `gemerkteRev` ist das des
 * laufenden Tabs (ein Ref, sofort da) und hält die Meldung von den
 * Durchläufen alle 45 s fern; `istZuAltGemeldet` liest den Merker aus der
 * IndexedDB und hält sie über NEUSTARTS hinweg fern — genau daran fehlte es,
 * weshalb derselbe Balken jeden Morgen und nach jedem Reload erneut stand.
 *
 * Reine Funktion, damit der Neustart ohne React und ohne IndexedDB prüfbar
 * ist (Muster `staleEntrySaveOptions`).
 */
export const zuAltNochZuMelden = (entry: QueueEntry, gemerkt: ZuAltKennung | undefined): boolean => {
    if (istZuAltGemeldet(entry)) return false;
    if (!gemerkt) return true;
    // Gemerkt wird je Projekt, gemeint ist aber ein STAND. `rev` allein
    // verwechselt zwei davon: sie zählt je Eintrag ab 0 und beginnt nach
    // einem Löschen wieder bei 0, und der Eintrag eines anderen Benutzers
    // zum selben Projekt zählt seine eigene. Ohne Benutzer und
    // Inhaltsstempel verschluckte der Merker fremde Absagen.
    return !(
        gemerkt.userId === entry.userId
        && gemerkt.rev === entry.rev
        && gemerkt.updatedAt === (entry.updatedAt ?? entry.queuedAt)
    );
};

/**
 * Ist das, was jetzt in der Warteschlange liegt, noch GENAU der Stand, über
 * den der gezeigte Hinweis spricht?
 *
 * Vier Bedingungen, jede einzeln nötig:
 *   • kein Eintrag mehr da → nichts zu tun;
 *   • nicht (mehr) vom Altersriegel angehalten → der Eintrag ist wieder
 *     unterwegs. `istZuAltBlockiert` prüft beide Hälften, Zustand UND
 *     Fehlercode: ein 'pending' mit altem Code 'zu-alt' (markSyncFailure
 *     schreibt den Code, der Zustand wandert) darf hier nicht durchrutschen;
 *   • andere `rev` → ein neuer Bearbeitungsgang liegt da, dessen Absage noch
 *     niemand gesehen hat;
 *   • anderes `updatedAt` → derselbe Gürtel, zweite Schnalle. `rev` zählt je
 *     Eintrag ab 0 und beginnt nach einem Löschen wieder bei 0; `updatedAt`
 *     ist der Zeitstempel des Inhalts, den `coalesceReportSave` bei jedem
 *     Speichern neu setzt. Gelesen wird er mit demselben Rückfall wie in
 *     `queueEntryAgeMs` (`?? queuedAt`), damit Bestandsdaten ohne
 *     `updatedAt` nicht grundsätzlich danebenliegen.
 *
 * Reine Funktion, damit dieser Schnitt ohne React und ohne IndexedDB prüfbar
 * ist (Muster `staleEntrySaveOptions`).
 */
export const passtZurKennung = (
    entry: QueueEntry | undefined,
    k: ZuAltKennung,
): entry is QueueEntry =>
    !!entry
    && istZuAltBlockiert(entry)
    && entry.rev === k.rev
    && (entry.updatedAt ?? entry.queuedAt) === k.updatedAt;

/**
 * Der Mutator hinter `acknowledgeTooOld` — den Merker „gesehen" setzen, aber
 * NUR auf genau den Stand, den der Anwender weggeklickt hat. Alles andere
 * bleibt unverändert (`undefined`).
 */
export const merkeNurDiesenStand = (
    entry: QueueEntry | undefined,
    k: ZuAltKennung,
): QueueEntry | undefined =>
    passtZurKennung(entry, k) ? markTooOldAnnounced(entry) : undefined;

/**
 * Der Mutator hinter `discardTooOldEntry` — `null` (löschen) ausschliesslich
 * für genau den angehaltenen Stand, der auf dem Bildschirm stand; in JEDEM
 * anderen Fall `undefined`.
 *
 * Zwischen Klick und Transaktion kann der Anwender längst wieder gespeichert
 * haben — `coalesceReportSave` macht aus dem angehaltenen Eintrag dann einen
 * frischen 'pending'. Würde der gelöscht, verschwände nie hochgeladene Arbeit
 * auf einen Knopf, der etwas ganz anderes versprochen hat.
 */
export const verwerfeNurDiesenStand = (
    entry: QueueEntry | undefined,
    k: ZuAltKennung,
): QueueEntry | null | undefined =>
    passtZurKennung(entry, k) ? null : undefined;

/**
 * Was beim Öffnen und auf dem Rückweg mit dem liegenden Eintrag geschieht
 * (Lücke O1, 21.10.2026):
 *   'entwurf'  — er wird auf den Serverstand gelegt;
 *   'anhalten' — er ist zu alt und noch nicht angehalten: anhalten, melden,
 *                Serverstand zeigen;
 *   'server'   — kein Eintrag, schon angehalten, oder zu alt in einem Zustand,
 *                den der Riegel nicht anfasst ('syncing', anders blockiert):
 *                Serverstand zeigen.
 *
 * Reine Funktion, damit dieser Schnitt ohne React und ohne IndexedDB prüfbar
 * ist (Muster `staleEntrySaveOptions`).
 */
export const entwurfsEntscheid = (
    entry: QueueEntry | undefined,
    nowIso: string,
): 'entwurf' | 'anhalten' | 'server' => {
    if (lokalerStandZaehlt(entry, nowIso)) return 'entwurf';
    if (entry && !istZuAltBlockiert(entry) && darfAngehaltenWerden(entry) && istZuAlt(entry, nowIso)) return 'anhalten';
    return 'server';
};

/**
 * Der Mutator hinter dem Anhalten: `markTooOld` nur auf GENAU den gelesenen
 * Stand (`rev`), nur in einem Zustand, den der Riegel anhalten darf, und nur,
 * wenn er im Augenblick des Schreibens noch zu alt ist. Sonst `undefined`
 * (unverändert) — ein paralleles Speichern hat den Inhalt erneuert, oder ein
 * anderer Tab lädt ihn gerade hoch.
 */
export const halteNurDiesenStandAn = (
    e: QueueEntry | undefined,
    gelesen: QueueEntry,
    nowIso: string,
): QueueEntry | undefined =>
    e && e.rev === gelesen.rev && darfAngehaltenWerden(e) && istZuAlt(e, nowIso)
        ? markTooOld(e, nowIso)
        : undefined;

/**
 * Was der Durchlauf (`flushAll`) mit einem Eintrag tut:
 *   'melden'    — vom Altersriegel angehalten: je Stand einmal melden;
 *   'anhalten'  — abgewiesen und zu alt: anhalten wie jeden anderen;
 *   'erneut'    — abgewiesen, nur beim Start: einmal erneut versuchen;
 *   'hochladen' — wartet und darf jetzt (`canFlush`);
 *   'nichts'    — alles andere (fremder Benutzer, Wartezeit, eingefroren,
 *                 abgewiesen im regelmässigen Takt).
 *
 * Reine Funktion (Muster `staleEntrySaveOptions`).
 */
export const durchlaufEntscheid = (
    entry: QueueEntry,
    uid: string,
    nowIso: string,
    abgewieseneErneut: boolean,
): 'melden' | 'anhalten' | 'erneut' | 'hochladen' | 'nichts' => {
    if (entry.userId !== uid) return 'nichts';
    if (istZuAltBlockiert(entry)) return 'melden';
    if (istAbgewiesen(entry)) {
        // Ein abgewiesener Eintrag altert wie jeder andere: nach 24 h geht er
        // nicht mehr raus, auch nicht beim nächsten Start.
        if (istZuAlt(entry, nowIso)) return 'anhalten';
        return abgewieseneErneut ? 'erneut' : 'nichts';
    }
    return canFlush(entry, uid, nowIso) ? 'hochladen' : 'nichts';
};

/**
 * Den liegenden Eintrag fürs Öffnen holen und entscheiden — mit Wiederholung,
 * wenn das Anhalten ins Leere ging.
 *
 * Das Anhalten trifft nur genau den gelesenen Stand. Hat ein zweiter Tab
 * desselben Anwenders zwischen Lesen und Anhalten frisch gespeichert, geht es
 * zu Recht ins Leere — dann liegt aber ein FRISCHER Stand da, und der gehört
 * als Entwurf in den State. Ohne neues Lesen zeigte das Öffnen nur den
 * Serverstand, und der nächste Anschlag hier überschriebe den frischen
 * Inhalt in der Warteschlange (Codex-Review, 21.10.2026). Höchstens
 * `versuche` Runden; danach Serverstand — sicher, weil nichts Altes in den
 * State kommt.
 *
 * Lesen, Anhalten und Uhr werden hereingereicht, damit der Ablauf ohne React
 * und ohne IndexedDB prüfbar ist.
 */
export const holeEntwurfFuerOeffnen = async (
    lies: () => Promise<QueueEntry | undefined>,
    halteAn: (entry: QueueEntry, nowIso: string) => Promise<boolean>,
    jetzt: () => string,
    versuche = 3,
): Promise<QueueEntry | undefined> => {
    for (let i = 0; i < versuche; i++) {
        const entry = await lies();
        if (!entry) return undefined;
        const nowIso = jetzt();
        const entscheid = entwurfsEntscheid(entry, nowIso);
        if (entscheid === 'entwurf') return entry;
        if (entscheid === 'server') return undefined;
        // 'anhalten': angehalten → Serverstand. Ins Leere gegangen → der
        // Eintrag hat sich geändert, neu lesen und neu entscheiden.
        if (await halteAn(entry, nowIso)) return undefined;
    }
    return undefined;
};

/**
 * Der Fehlerabschluss eines Hochladens — `undefined`, wenn er den Eintrag
 * nicht anfassen darf (`darfAbschlussAnwenden`).
 *
 * Eine Absage der Datenbank gilt dem hochgeladenen INHALT. Hat der Anwender
 * während des Hochladens weitergeschrieben (`rev` höher), trägt der Eintrag
 * schon neuen Inhalt — womöglich genau die Korrektur (Projekttyp
 * zurückgestellt). Den als 'abgewiesen' abzustellen, hielte ihn bis zur
 * nächsten Änderung oder zum nächsten Start fest (Codex-Review,
 * 21.10.2026). Er geht stattdessen ohne Laufmarkierung zurück in die
 * Warteschlange und mit dem nächsten Anlass hinaus; scheitert er auch, wird
 * DANN abgewiesen und gemeldet. Für die übrigen Fehlerarten bleibt es beim
 * bisherigen Abschluss.
 */
export const fehlerAbschluss = (
    current: QueueEntry | undefined,
    geflusht: AbschlussKennung,
    kind: SyncFailureKind,
    fehler: unknown,
    nowIso: string,
    random: () => number,
): { eintrag: QueueEntry; freigegeben: boolean } | undefined => {
    if (!current || !darfAbschlussAnwenden(current, geflusht)) return undefined;
    if (kind === 'abgewiesen' && current.rev !== geflusht.rev) {
        return { eintrag: zurueckInDieWarteschlange(current), freigegeben: true };
    }
    return { eintrag: markSyncFailure(current, kind, fehler, nowIso, random), freigegeben: false };
};

/** Der Mutator hinter 'erneut': nur genau dieser Stand, und nur, wenn er noch abgewiesen ist. */
export const erneutNurDiesenStand = (
    e: QueueEntry | undefined,
    gelesen: QueueEntry,
): QueueEntry | undefined =>
    e && e.rev === gelesen.rev && istAbgewiesen(e) ? abgewiesenErneutVersuchen(e) : undefined;

/**
 * Was die Anzeige nach einem gescheiterten Hochladen zeigt.
 *
 * 'offline' auch dann, wenn das Gerät sich für online hält, der Speicherweg
 * aber mit einem NetzFehler abgebrochen hat: im Funkloch mit Empfangsbalken
 * und in der App bleibt `navigator.onLine` true, und „Wartet auf Sync" sagte
 * nicht, dass das Netz fehlt. Bei gestörtem Auth-Server gilt dasselbe — der
 * Server ist nicht erreichbar, gesichert ist lokal. 'offline' und 'queued'
 * behandelt die Oberfläche sonst gleich (gelb, noch nicht auf dem Server).
 *
 * Reine Funktion, damit sie ohne React prüfbar ist (Muster
 * `staleEntrySaveOptions`).
 */
export const statusNachFehlschlag = (
    kind: SyncFailureKind,
    fehler: unknown,
    geraetOffline: boolean,
): SaveStatus => {
    if (kind === 'auth-required') return 'auth-required';
    // „Wartet auf Sync" wäre gelogen: dieser Inhalt geht so nicht durch.
    if (kind === 'abgewiesen') return 'error';
    return geraetOffline || istEigenerNetzFehler(fehler) ? 'offline' : 'queued';
};

/** Was `verdrahteSofortigeSicherung` vom Fenster und vom Dokument braucht. */
interface SeitenEreignisse {
    addEventListener: (typ: string, fn: () => void) => void;
    removeEventListener: (typ: string, fn: () => void) => void;
}

/**
 * Die Ausloeser, bei denen das Buendel der lokalen Sicherung (300 ms Ruhe,
 * spaetestens 2 s — offlineQueueLogic.erzeugeSchreibbuendel) NICHT warten
 * darf, sondern sofort schreibt:
 *
 *   • `visibilitychange` nach 'hidden' — auf dem Handy der letzte Augenblick,
 *     in dem die Seite nachweislich laeuft (App-Wechsel in Kamera oder
 *     Telefon; danach raeumt das Betriebssystem ab, ohne `pagehide`).
 *   • `pagehide` — Tab zu, Neuladen, Back-Forward-Cache. Auch das
 *     In-App-Update loest es vor der Installation eigens aus
 *     (appUpdate.seiteVerlassenFuerUpdate).
 *   • `blur` am Fenster — der Anwender wechselt in ein anderes Fenster oder
 *     einen anderen Tab. Der Grund ist der zweite Tab DESSELBEN Anwenders am
 *     selben Projekt (Befund der Pruefung, 30.09.2026): die Projektsperre
 *     trennt zwei Tabs desselben Benutzers nicht, und beide schreiben in
 *     denselben Eintrag [userId, projectId]. Haelt Tab A noch bis zu 2 s
 *     einen Stand im Buendel, waehrend in Tab B schon weitergetippt und
 *     gesichert wird, landete As aelterer Stand danach UEBER dem von B. Mit
 *     `blur` liegt As Stand fest, sobald der Anwender hinueberwechselt.
 *
 * RESTRISIKO, bewusst stehen gelassen: zwischen zwei Tabs desselben
 * Anwenders am selben Projekt gilt weiter „wer zuletzt schreibt, gewinnt" —
 * das war vor der Buendelung genauso. Enger geworden ist es nicht ueberall:
 * aendert sich der Bericht im NICHT fokussierten Tab ohne Tippen (spaetes
 * Ergebnis eines Foto-Uploads, `spaetAendern`, Uebernahme nach einem Flush),
 * haelt dieser Tab den Stand bis zu 2 s, und kein `blur` kommt mehr; ebenso
 * wo ein Browser `blur` nicht zustellt (geteilter Bildschirm auf dem Handy).
 * Vorher lag dieses Fenster bei Null. Das Hochladen schuetzen Anker und
 * Zusammenfuehrung wie bisher; es geht um die Reihenfolge der lokalen
 * Staende.
 *
 * Ob die Transaktion beim Entladen noch durchkommt, entscheidet der Browser;
 * angestossen wird sie hier so frueh wie moeglich. Rueckgabe: das Abmelden.
 *
 * Reine Funktion, damit die Verdrahtung ohne React und ohne Browser pruefbar
 * ist (Muster `staleEntrySaveOptions`).
 */
export const verdrahteSofortigeSicherung = (
    fenster: SeitenEreignisse,
    dokument: SeitenEreignisse & { visibilityState: string },
    schreibeJetzt: () => void,
): (() => void) => {
    const beiVerdeckt = () => { if (dokument.visibilityState === 'hidden') schreibeJetzt(); };
    const beiVerlassen = () => { schreibeJetzt(); };
    dokument.addEventListener('visibilitychange', beiVerdeckt);
    fenster.addEventListener('pagehide', beiVerlassen);
    fenster.addEventListener('blur', beiVerlassen);
    return () => {
        dokument.removeEventListener('visibilitychange', beiVerdeckt);
        fenster.removeEventListener('pagehide', beiVerlassen);
        fenster.removeEventListener('blur', beiVerlassen);
    };
};

export const useOfflineSync = ({ userId, saveReport, loadServerReport, onMerged, onStatus, onFlushed, canUpload, onUploadBlocked, onBuildOutdated }: Options) => {
    const userIdRef = useRef(userId);
    userIdRef.current = userId;
    const flushingRef = useRef<Set<string>>(new Set());
    // Die Zahl der offenen lokalen Sicherungen fuehrt seit dem 30.09.2026
    // das Buendel selbst (offlineQueue.lokaleSicherungOffen): dort liegt der
    // noch nicht geschriebene Stand, und nur dort ist zu sehen, ob er wartet
    // oder schon geschrieben wird.
    const saveReportRef = useRef(saveReport); saveReportRef.current = saveReport;
    const loadServerReportRef = useRef(loadServerReport); loadServerReportRef.current = loadServerReport;
    const onMergedRef = useRef(onMerged); onMergedRef.current = onMerged;
    const onStatusRef = useRef(onStatus); onStatusRef.current = onStatus;
    const onFlushedRef = useRef(onFlushed); onFlushedRef.current = onFlushed;
    const canUploadRef = useRef(canUpload); canUploadRef.current = canUpload;
    const onUploadBlockedRef = useRef(onUploadBlocked); onUploadBlockedRef.current = onUploadBlocked;
    const onBuildOutdatedRef = useRef(onBuildOutdated); onBuildOutdatedRef.current = onBuildOutdated;

    /**
     * Wem gegenüber die Absage schon gemeldet wurde, je Projekt. Ein Ref, kein
     * State: es darf kein Render davon abhängen, und beim nächsten Flush muss
     * der Wert ohne Umweg zur Verfügung stehen.
     */
    const announcedHolderRef = useRef<Map<string, string>>(new Map());

    /**
     * Welche Revision je Projekt schon als „zu alt" gemeldet wurde — das
     * schnelle Gedächtnis dieses Tabs. flushAll läuft alle 45 s und bei jedem
     * Sichtbarwerden des Tabs; ohne diesen Ref poppte dieselbe Meldung im
     * Minutentakt neu auf.
     *
     * Er allein reicht aber nicht: ein Ref stirbt mit dem Tab, und ein
     * angehaltener Eintrag ändert sich nie wieder. Bis zum 22.09.2026 stand
     * derselbe Balken deshalb bei jedem Start erneut da — wer das betroffene
     * Projekt nie wieder anfasste, bekam ihn täglich. Das dauerhafte
     * Gedächtnis liegt neben dem Eintrag selbst (`zuAltGemeldetRev`) und wird
     * NICHT hier geschrieben, sondern erst, wenn der Anwender den Hinweis
     * wirklich weggeklickt hat (`acknowledgeTooOld`). Sonst verstummten bei
     * mehreren alten Einträgen alle bis auf den zuletzt gemeldeten: der
     * Aufrufer zeigt einen Balken nach dem anderen, gemerkt wären aber
     * sofort alle.
     *
     * NUR Sitzungs-Dedupe, kein Schlüssel für irgendeine Schreiboperation:
     * was der Anwender am Balken anfasst, wird über die `ZuAltKennung`
     * angefasst, die mit der Meldung mitgegangen ist. Gespeichert wird
     * trotzdem die ganze Kennung und nicht bloss die Revision: zwei Stände
     * desselben Projekts können dieselbe `rev` tragen (anderer Benutzer,
     * oder ein nach dem Löschen neu angelegter Eintrag, der wieder bei 0
     * zählt), und der Merker verschluckte sonst eine fremde Absage.
     *
     * Ein Eintrag wird hier NICHT entfernt, wenn der Stand verworfen wurde —
     * die Begründung steht an `discardTooOldEntry`. Geleert wird der Ref nur
     * beim Benutzerwechsel.
     */
    const announcedTooOldRef = useRef<Map<string, ZuAltKennung>>(new Map());

    /**
     * Meldet einen angehaltenen Eintrag genau einmal je Stand — auch über
     * Neustarts hinweg (dafür sorgt der Merker, den `acknowledgeTooOld`
     * schreibt).
     *
     * Mit auf den Weg geht die Kennung genau dieses Standes. Sie ist alles,
     * was der Aufrufer später braucht, und das einzige, womit er den Stand
     * wiederfindet.
     */
    const announceTooOld = useCallback((entry: QueueEntry) => {
        // Ein Durchlauf, der vor dem Benutzerwechsel begonnen hat, kommt mit
        // Einträgen der VORIGEN Sitzung zurück (die Abfrage lief asynchron,
        // die Antwort kommt später). Wer jetzt am Gerät sitzt, bekäme einen
        // Balken über die Arbeit von jemand anderem.
        if (entry.userId !== userIdRef.current) return;
        if (!zuAltNochZuMelden(entry, announcedTooOldRef.current.get(entry.projectId))) return;
        const kennung: ZuAltKennung = {
            userId: entry.userId,
            projectId: entry.projectId,
            rev: entry.rev,
            // Derselbe Rückfall wie in `queueEntryAgeMs`: Bestandsdaten
            // ohne `updatedAt` tragen nur `queuedAt`.
            updatedAt: entry.updatedAt ?? entry.queuedAt,
        };
        announcedTooOldRef.current.set(entry.projectId, kennung);
        onUploadBlockedRef.current?.(
            entry.projectId,
            entry.lastErrorMessage
                ?? `Nicht übertragen: Ihre lokale Änderung am Projekt „${queueEntryProjectLabel(entry)}" war zu alt.`,
            'zu-alt',
            kennung,
        );
    }, []);

    /**
     * Den Altersriegel an GENAU diesem Stand vollziehen: atomar, nur in der
     * gelesenen Revision und nur in einem Zustand, den er anhalten darf
     * (`darfAngehaltenWerden`). Hat ein paralleles Speichern den Inhalt
     * inzwischen erneuert, ist er frisch, und nichts geschieht.
     *
     * Gebraucht beim Hochladen, beim Öffnen und auf dem Rückweg
     * (`lokalenEntwurfHolen`) und für abgewiesene Einträge im Durchlauf.
     * `true`, wenn angehalten wurde.
     */
    const halteZuAltAn = useCallback(async (uid: string, entry: QueueEntry, nowIso: string): Promise<boolean> => {
        let angehalten: QueueEntry | undefined;
        await mutateQueueEntry(uid, entry.projectId, e => {
            angehalten = halteNurDiesenStandAn(e, entry, nowIso);
            return angehalten;
        }).catch(() => {});
        if (!angehalten) return false;
        console.warn('[offlineSync] Eintrag zu alt, nicht hochgeladen:', entry.projectId, (angehalten as QueueEntry).lastErrorMessage);
        announceTooOld(angehalten);
        // 'error' und nicht 'queued': „Wartet auf Sync" wäre gelogen,
        // dieser Stand geht nicht mehr raus.
        onStatusRef.current('error');
        return true;
    }, [announceTooOld]);

    /**
     * Der liegende Eintrag dieses Projekts — aber nur, wenn er als Entwurf
     * auf den Serverstand gelegt werden darf (App.tsx beim Öffnen und auf dem
     * Rückweg nach einer Sperre).
     *
     * Ist er älter als der Altersriegel erlaubt, wird er HIER angehalten und
     * gemeldet, statt in den State zu fliessen (Lücke O1, 21.10.2026): bis
     * dahin prüfte das Öffnen nur die Markierung, und die setzt erst das
     * Hochladen. Ein wochenalter Eintrag ohne Ausgangsstand kam so als Entwurf
     * in den State, das nächste Speichern traf den frischen Anker und glich
     * die Kindtabellen gegen den alten Stand ab (6223, 5882, 6185).
     *
     * `undefined` heisst: Serverstand zeigen. Lesefehler zählen wie „kein
     * Eintrag" — wie bisher an den Aufrufstellen.
     */
    const lokalenEntwurfHolen = useCallback(async (projectId: string): Promise<QueueEntry | undefined> => {
        const uid = userIdRef.current;
        if (!uid) return undefined;
        return holeEntwurfFuerOeffnen(
            () => getQueueEntry(uid, projectId).catch(() => undefined),
            (entry, nowIso) => halteZuAltAn(uid, entry, nowIso),
            () => new Date().toISOString(),
        );
    }, [halteZuAltAn]);

    /**
     * Der Anwender hat den Hinweis gesehen und weggeklickt — ab jetzt
     * dauerhaft still für genau diesen Stand.
     *
     * Erst hier, nicht schon beim Melden: liegen drei alte Einträge in der
     * Warteschlange, meldet `flushAll` alle drei, sichtbar ist aber immer nur
     * einer. Würde beim Melden gemerkt, wären die anderen beiden für immer
     * stumm, ohne dass sie je jemand gelesen hat.
     *
     * Adressiert wird ausschliesslich über die Kennung — Benutzer, Projekt,
     * Revision und Inhaltsstempel des gezeigten Standes. Nichts davon kommt
     * aus dem Ref oder aus der laufenden Sitzung: der Balken kann älter sein
     * als alles, was gerade in der Warteschlange liegt. Fehler werden
     * geschluckt; der Preis einer gescheiterten Transaktion ist eine Meldung
     * zu viel beim nächsten Start — nie eine zu wenig.
     */
    const acknowledgeTooOld = useCallback(async (k: ZuAltKennung): Promise<void> => {
        await mutateQueueEntry(k.userId, k.projectId, e => merkeNurDiesenStand(e, k))
            .catch(() => {});
    }, []);

    /**
     * Den angehaltenen lokalen Stand wegwerfen — der einzige Weg, den
     * Altersriegel loszuwerden, ohne das Projekt anzufassen.
     *
     * Gelöscht wird ausschliesslich der Stand, der auf dem Bildschirm stand
     * (`verwerfeNurDiesenStand` prüft Zustand, Revision und Inhaltsstempel);
     * alles andere bleibt unangetastet, und `false` sagt genau das. Der
     * Schlüssel ist `k.userId`, nicht der gerade angemeldete Benutzer — sonst
     * träfe ein Klick nach einem Benutzerwechsel den Eintrag des Falschen.
     *
     * Am Server ändert das nichts: was dort steht, ist ohnehin der Stand, den
     * die App anzeigt — der lokale Beleg war nur noch Beleg.
     */
    const discardTooOldEntry = useCallback(async (k: ZuAltKennung): Promise<boolean> => {
        let geloescht = false;
        try {
            await mutateQueueEntry(k.userId, k.projectId, e => {
                const ergebnis = verwerfeNurDiesenStand(e, k);
                if (ergebnis === null && e) {
                    geloescht = true;
                    // Die einzige Spur, die von diesem Stand bleibt. Was hier
                    // gelöscht wird, ist nirgends sonst protokolliert — ohne
                    // diese Zeile liesse sich hinterher nicht mehr sagen, WAS
                    // der Anwender weggeworfen hat.
                    console.warn('[offlineSync] Lokalen Stand verworfen', k.projectId, {
                        entryId: e.entryId, rev: e.rev, updatedAt: e.updatedAt,
                    });
                }
                return ergebnis;
            });
        } catch {
            return false;
        }
        // Der Sitzungsmerker bleibt ABSICHTLICH stehen, obwohl der Eintrag
        // weg ist: `flushAll` arbeitet auf einem Abzug der Warteschlange.
        // Liest er A und B, wartet dann minutenlang am Upload von A und wird
        // B derweil hier verworfen, meldet der alte Abzug B gleich darauf
        // erneut — ein Balken über einen Stand, den es nicht mehr gibt, und
        // ein zweites Verwerfen daran scheitert für immer (`false`, der
        // Hinweis bliebe stehen). Mit der Kennung im Ref greift der
        // Sitzungs-Dedupe und die Geistermeldung unterbleibt. Ein wirklich
        // neuer Stand kommt trotzdem durch: `zuAltNochZuMelden` vergleicht
        // Benutzer, Revision und Inhaltsstempel, und die stimmen dann nicht
        // mehr überein.
        return geloescht;
    }, []);

    /** Torwächter fragen — mit Fail-open um Fehlen und Fehler herum. */
    const askUploadGate = useCallback(async (projectId: string): Promise<QueueUploadDecision> => {
        const gate = canUploadRef.current;
        if (!gate) return { upload: true, reason: 'ok' };
        try {
            return await gate(projectId);
        } catch (e) {
            console.warn('[offlineSync] Sperrprüfung fehlgeschlagen — wird hochgeladen:', e);
            return { upload: true, reason: 'ok' };
        }
    }, []);

    /**
     * Sichert den Report lokal. Liefert false bei Speicherfehler (UI muss warnen).
     *
     * GEBÜNDELT seit dem 30.09.2026 (Entscheid des Betreibers): geschrieben
     * wird nach 300 ms Ruhe, beim Durchtippen spätestens alle 2 s, und sofort
     * bei jedem Lesen der Warteschlange, beim Verdecken/Verlassen der Seite
     * und beim Abbau (Regel: offlineQueueLogic.erzeugeSchreibbuendel). Der
     * Vertrag bleibt: das Versprechen löst erst auf, wenn der Stand — oder
     * ein neuerer desselben Projekts aus demselben Fenster — dauerhaft in der
     * IndexedDB steht. Wer `true` bekommt, dessen Eingabe ist gesichert.
     *
     * `base` ist der Serverstand, aus dem der Bearbeitungsgang hervorgegangen
     * ist. Er wird nur beim ERSTEN Einreihen übernommen (siehe
     * `coalesceReportSave`; innerhalb eines Bündelfensters zählt ebenso der
     * erste Aufruf) und entscheidet später darüber, ob sich ein überholter
     * Eintrag zusammenführen lässt oder nur bergen.
     */
    const enqueue = useCallback(async (
        projectId: string,
        report: Report,
        base?: { report: Report; updatedAt: string | null } | null,
    ): Promise<boolean> => {
        const uid = userIdRef.current;
        if (!uid) return false;
        try {
            await putLatestGebuendelt({
                userId: uid, projectId, report,
                base: base?.report,
                baseUpdatedAt: base?.updatedAt ?? null,
            });
            return true;
        } catch {
            onStatusRef.current('storage-error');
            return false;
        }
    }, []);

    /**
     * Alles, was im Bündel der lokalen Sicherung wartet, sofort schreiben und
     * abwarten. WIRFT, wenn danach ein Stand ungesichert bleibt (Schreiben
     * gescheitert) — „gesichert" darf nur hören, wer gesichert ist.
     *
     * Die Lese- und Schreibwege der Warteschlange tun das ohnehin selbst
     * (Kette in offlineQueue.ts) — `flushProject` und `flushAll` laden also
     * immer den jüngsten Stand hoch. Von aussen gebraucht wird es dort, wo
     * nichts gelesen wird, der Stand aber trotzdem sofort fest liegen muss.
     */
    const flushPending = useCallback((): Promise<void> => schreibeWartendeJetzt(), []);

    /**
     * Der überholte Eintrag — was statt „drüberschreiben" geschieht.
     *
     * Rückgabe: die Serverzeile und der Stand, der wirklich geschrieben wurde
     * (den braucht der State-Merge in App.handleQueueFlushed — nach einer
     * Zusammenführung ist das NICHT mehr `entry.report`).
     *
     * Wirft weiter, wenn kein Serverstand zu holen ist: der Eintrag bleibt
     * dann in der Warteschlange und wird später erneut versucht. Das ist
     * ausdrücklich besser als der bisherige Ausgang, bei dem der ältere Stand
     * gewonnen hat.
     */
    const resolveStaleEntry = useCallback(async (
        entry: QueueEntry,
        ursprung: unknown,
    ): Promise<{ row: any; savedReport: Report }> => {
        const loader = loadServerReportRef.current;
        if (!loader) throw ursprung;
        const server = await loader(entry.projectId);
        if (!server?.report) throw ursprung;

        const result = entry.base
            ? mergeReports(entry.base, entry.report, server.report)
            : salvageForeignLogEntries(entry.report, server.report);
        const mode: MergeReport['mode'] = entry.base ? 'merge' : 'salvage';

        // Frischer Anker, Konflikt als Signal, Zähler vom Server — die
        // Begründung steht an `staleEntrySaveOptions`. Bei der Bergung (kein
        // Ausgangsstand) zusätzlich: nichts löschen, was der Server führt und
        // der Eintrag nie gesehen hat (6185, 16.09.2026).
        const row = await saveReportRef.current(
            { ...entry, report: result.merged },
            staleEntrySaveOptions(server.updatedAt, mode === 'salvage'),
        );
        onMergedRef.current?.(entry.projectId, {
            mode,
            conflicts: result.conflicts,
            keptForeignLogEntries: result.keptForeignLogEntries,
            removedLogEntries: result.removedLogEntries,
            ticketNummernWechsel: result.ticketNummernWechsel,
        });
        return { row, savedReport: result.merged };
    }, []);

    const flushProject = useCallback(async (projectId: string): Promise<any | null> => {
        const uid = userIdRef.current;
        if (!uid) { onStatusRef.current('auth-required'); return null; }
        if (flushingRef.current.has(projectId)) return null;
        // ─── Reservieren VOR dem ersten await ────────────────────────────
        // Prüfen und Eintragen müssen im selben Zug geschehen. Lag der
        // Eintrag dazwischen (Lesen der Warteschlange ist asynchron), kamen
        // zwei Aufrufe an derselben Prüfung vorbei und teilten sich danach
        // EINEN Set-Eintrag: das erste Löschen im finally meldete Ruhe,
        // obwohl der zweite Durchlauf noch hochlud — und genau auf diese
        // Auskunft stützt sich der Reload nach einem Sitzungsentzug.
        //
        // Die Reservierung deckt damit auch den Altersriegel unten ab, der
        // ebenfalls schreibt (markTooOld).
        flushingRef.current.add(projectId);
        try {
            // Liest NACH dem Schreiben des Bündels (Barriere in
            // offlineQueue.ts): der entprellte Cloud-Flush aus
            // App.handleUpdateReport lädt damit den jüngsten Anschlag hoch,
            // nicht den Stand von vor 300 ms. Die Reservierung oben deckt
            // diese Wartezeit mit ab (`istBeschaeftigt`).
            const entry = await getQueueEntry(uid, projectId).catch(() => undefined);
            if (!entry) return null;
            // Das letzte lokale Schreiben ist gescheitert (Speicher voll,
            // abgebrochene Transaktion): der jüngste Stand liegt NUR im
            // Speicher, in der IndexedDB steht ein älterer. Hochgeladen würde
            // genau dieser ältere — und `starteFlush` markierte ihn, nicht den
            // gelesenen. Also nicht hochladen, sondern sagen, was los ist;
            // der nächste Anlass versucht das Schreiben erneut, danach geht
            // der Stand ganz normal hinaus.
            if (lokalerStandUngesichert(uid, projectId)) {
                console.warn('[offlineSync] Lokale Sicherung gescheitert — nicht hochgeladen:', projectId);
                onStatusRef.current('storage-error');
                return null;
            }
            const nowIso = new Date().toISOString();
            if (!canFlush(entry, uid, nowIso)) return null;

            // ─── Altersriegel ────────────────────────────────────────────────
            // VOR dem Torwächter und vor markSyncStart: ein tagealter Eintrag
            // wird gar nicht erst zum Hochladen angefasst (07.09.2026: alte
            // Zähler über frische Buchungen, 24 Projekte). Atomar und nur auf
            // GENAU diese Revision — hat ein paralleles putLatest den Inhalt
            // inzwischen erneuert, ist er frisch und der nächste Trigger nimmt
            // ihn ganz normal mit (halteZuAltAn).
            if (istZuAlt(entry, nowIso)) {
                await halteZuAltAn(uid, entry, nowIso);
                return null;
            }

            // Kennung und Inhalt des Laufs — VOR dem try, weil auch der
            // Fehlerabschluss im catch die Kennung braucht. Beide werden von
            // `starteFlush` gesetzt und stammen dann aus EINEM Eintrag.
            let geflusht: AbschlussKennung = { rev: entry.rev };
            let hochzuladen: QueueEntry = entry;

            try {
                // ─── Torwächter ──────────────────────────────────────────────
                // VOR markSyncStart: ein abgewiesener Eintrag bleibt 'pending' und
                // wird damit vom nächsten Trigger (online, Tab sichtbar, 45-s-
                // Intervall) ganz normal wieder aufgegriffen. Nichts geht verloren,
                // nichts braucht eine Entscheidung des Nutzers.
                const gate = await askUploadGate(projectId);
                if (!gate.upload) {
                    if (gate.reason === 'locked'
                        && shouldAnnounceUploadBlock(announcedHolderRef.current.get(projectId), gate.holderId)) {
                        announcedHolderRef.current.set(projectId, gate.holderId);
                        // 'sperre': geht von selbst raus, sobald das Projekt
                        // frei ist — hier gibt es nichts zu verwerfen.
                        onUploadBlockedRef.current?.(projectId, gate.message, 'sperre');
                    }
                    onStatusRef.current('queued');
                    return null;
                }
                // Freie Bahn: die nächste Absage darf wieder gemeldet werden.
                announcedHolderRef.current.delete(projectId);

                onStatusRef.current('saving');
                // ─── Lauf eröffnen ───────────────────────────────────────────
                // Atomar nur den Zustand markieren — nie einen gelesenen
                // Snapshot zurückschreiben (Race mit parallelem putLatest).
                //
                // Inhalt UND Kennung kommen aus demselben Eintrag, den dieser
                // Schreibvorgang festhält: `entry` von oben ist zu diesem
                // Zeitpunkt womöglich überholt (der Torwächter durfte warten,
                // der Anwender hat inzwischen gespeichert, coalesce zählte rev
                // hoch). Lüde man weiter `entry` hoch und schlösse mit der
                // neuen Kennung ab, löschte der Abschluss eine nie
                // hochgeladene Revision.
                //
                // Nebenbei bekommt ein Eintrag aus einem alten Build hier
                // seine Identität (normalizeQueueEntry im Mutator) — auch
                // deshalb zählt nur, was dieser Schreibvorgang persistiert.
                let gestartet: FlushStart | null = null;
                await mutateQueueEntry(uid, projectId, e => {
                    gestartet = starteFlush(e, new Date().toISOString(), erzeugeEintragsId());
                    return gestartet?.eintrag;
                });
                if (!gestartet) {
                    // Der Eintrag ist zwischen Lesen und Markieren verschwunden
                    // — ein anderer Tab war mit genau diesem Stand schneller.
                    // Es gibt nichts hochzuladen; ein Upload aus `entry` wäre
                    // ein Stand, den die Warteschlange nicht mehr kennt.
                    onStatusRef.current('saved');
                    return null;
                }
                geflusht = (gestartet as FlushStart).kennung;
                hochzuladen = (gestartet as FlushStart).eintrag;

                // ─── Hochladen, mit Anker ────────────────────────────────────
                // Kennt der Eintrag seinen Ausgangsstand, wird gegen ihn
                // geschrieben; trifft er nicht, ist der Serverstand weiter als
                // dieser Eintrag und es wird zusammengeführt statt überschrieben.
                //
                // Ein Alteintrag ohne Anker (aus einer Version vor dem 31.08.2026)
                // kann nichts anderes als bedingungslos schreiben. Für ihn wird
                // VORHER geborgen: der Serverstand wird geholt und jede
                // Protokollzeile, die er nicht kennt, in seinen Stand übernommen.
                // Sonst nimmt genau dieser Eintrag die Buchungen mit, um die es
                // hier geht.
                let row: any = null;
                let savedReport: Report = hochzuladen.report;
                if (hochzuladen.baseUpdatedAt) {
                    try {
                        // Sparsam NUR hier, im Normalfall: der Eintrag setzt
                        // auf seinem eigenen Ausgangsstand auf, und ein
                        // Konflikt wirft. Unveraenderte Kindtabellen gehen
                        // nur dann nicht mit, wenn der Tab nachweisen kann,
                        // dass der Server sie schon so haelt (childSaveDelta.ts).
                        // Zusammenfuehren und Bergen unten schreiben voll.
                        row = await saveReportRef.current(hochzuladen, { expectedUpdatedAt: hochzuladen.baseUpdatedAt, onConflict: 'throw', uebertragung: 'sparsam' });
                    } catch (e) {
                        if (!isProjectSaveConflict(e)) throw e;
                        const geloest = await resolveStaleEntry(hochzuladen, e);
                        row = geloest.row;
                        savedReport = geloest.savedReport;
                    }
                } else if (loadServerReportRef.current) {
                    const geborgen = await resolveStaleEntry(hochzuladen, new Error(
                        'Warteschlangeneintrag ohne Ausgangsstand — Bergung fehlgeschlagen',
                    ));
                    row = geborgen.row;
                    savedReport = geborgen.savedReport;
                } else {
                    row = await saveReportRef.current(hochzuladen, { anchor: 'off' });
                }

                // Genau den geflushten Eintrag in genau dieser Revision
                // entfernen; eine während des Flush koaleszierte neuere
                // Revision bleibt pending. Liegt inzwischen ein ANDERER Eintrag
                // da (ein zweiter Tab hat diesen hier freigegeben, hochgeladen
                // und gelöscht, der Anwender hat weitergetippt), wird er nicht
                // angefasst — er war nie hochgeladen.
                let removed = false;
                await mutateQueueEntry(uid, projectId, e => {
                    if (!darfAbschlussAnwenden(e, geflusht)) return undefined;
                    if (darfAbschlussLoeschen(e, geflusht)) { removed = true; return null; }
                    // Eigener Eintrag, aber neuere Revision: nur den Lauf
                    // beenden, der Inhalt geht beim nächsten Mal hoch.
                    return zurueckInDieWarteschlange(e!);
                });
                onStatusRef.current(removed ? 'saved' : 'queued');
                // NICHT `entry.report`: nach einer Zusammenführung ist der
                // geschriebene Stand ein anderer, und der State übernähme sonst
                // genau den überholten Stand, den der Server eben abgewiesen hat.
                if (row) onFlushedRef.current(projectId, row, savedReport);
                return row;
            } catch (e) {
                const kind = classifySyncError(e);
                // Der Build-Riegel des Servers ist der HARTE Befund: bis hier
                // hat sich dieses Bundle fuer aktuell gehalten (sonst haette
                // der Torwaechter den Flush gar nicht durchgelassen). Der
                // Befund wird sofort vermerkt, damit auch jeder andere
                // Schreibweg stillsteht — nicht erst beim naechsten
                // /version.json-Abgleich, der in einem Tab mit halb totem
                // Netzpfad nie kommt.
                if (kind === 'build-veraltet') {
                    markiereBuildVeraltet();
                    onBuildOutdatedRef.current?.();
                }
                const nowIso = new Date().toISOString();
                // Auch der Fehlerabschluss nur auf den eigenen Eintrag: einem
                // inzwischen neu angelegten Eintrag einen Fehlerzustand samt
                // Wartezeit anzuhängen, hielte frische Arbeit ohne Anlass fest.
                let abschluss: { eintrag: QueueEntry; freigegeben: boolean } | undefined;
                await mutateQueueEntry(uid, projectId, current => {
                    abschluss = fehlerAbschluss(current, geflusht, kind, e, nowIso, Math.random);
                    return abschluss?.eintrag;
                }).catch(() => {});
                const ergebnis = abschluss as { eintrag: QueueEntry; freigegeben: boolean } | undefined;
                // Neuerer Inhalt kam während des Hochladens dazu und wartet
                // wieder (fehlerAbschluss) — er geht mit dem nächsten Anlass
                // hinaus; über ihn ist noch nichts gesagt.
                if (ergebnis?.freigegeben) {
                    onStatusRef.current('queued');
                    return null;
                }
                // Die Absage der Datenbank kommt auf den Bildschirm: sie heilt
                // sich nicht durch Warten, und „Wartet auf Sync" verschwiege,
                // dass der Server diesen Inhalt ablehnt (22007, Meyer,
                // 26.08.–07.09.2026: wochenlang still wiederholt).
                if (kind === 'abgewiesen' && ergebnis) {
                    console.warn('[offlineSync] Von der Datenbank abgewiesen:', projectId, ergebnis.eintrag.lastErrorMessage);
                    onUploadBlockedRef.current?.(projectId, abgewiesenMeldung(ergebnis.eintrag), 'abgewiesen');
                }
                const offline = typeof navigator !== 'undefined' && !navigator.onLine;
                onStatusRef.current(statusNachFehlschlag(kind, e, offline));
                return null;
            }
        } finally {
            flushingRef.current.delete(projectId);
        }
    }, [askUploadGate, resolveStaleEntry, halteZuAltAn]);

    /**
     * Alle wartenden Einträge des Benutzers hochladen.
     *
     * `abgewieseneErneut`: von der Datenbank abgewiesene Einträge EINMAL
     * erneut versuchen — nur beim Start bzw. bei der Anmeldung. Ein neuer
     * Programmstand behebt den Grund womöglich (22007 war ein Fehler der App);
     * ohne diesen Anlass gingen sie erst mit der nächsten Änderung am Projekt
     * wieder raus. Nicht im 45-s-Takt: das wäre die endlose Wiederholung, die
     * 'abgewiesen' gerade abstellt.
     */
    const flushAll = useCallback(async (optionen?: { abgewieseneErneut?: boolean }) => {
        const uid = userIdRef.current;
        if (!uid) return;
        let entries = await listQueueEntries(uid).catch(() => [] as QueueEntry[]);
        const nowIso = new Date().toISOString();

        // ─── Verwaiste 'syncing'-Einträge zurückholen ────────────────────
        // Ein Tab, der zwischen markSyncStart und dem Aufräumen stirbt
        // (Reload nach Sitzungsentzug, geschlossener Tab, Absturz), lässt
        // seinen Eintrag in 'syncing' liegen — und `canFlush` unten nimmt nur
        // 'pending'. Das ist nie hochgeladene Arbeit und heilt von selbst
        // nicht. Hier, im regelmäßigen Durchlauf, und NICHT pauschal beim
        // Start: beim Start wüsste niemand, ob ein zweiter Tab gerade wirklich
        // hochlädt.
        const verwaiste = verwaisteSyncsZumFreigeben(
            entries, id => flushingRef.current.has(id), nowIso,
        );
        if (verwaiste.length) {
            for (const entry of verwaiste) {
                // Erneut prüfen IM Mutator: zwischen Auflisten und Schreiben
                // kann ein anderer Tab den Eintrag längst abgeschlossen haben.
                await mutateQueueEntry(uid, entry.projectId, e =>
                    e && istVerwaisterSync(e, nowIso) ? releaseOrphanedSync(e) : undefined,
                ).catch(() => {});
                console.warn('[offlineSync] Verwaisten Sync zurueckgeholt:', entry.projectId);
            }
            // Neu lesen, sonst trägt die Liste unten noch den alten Zustand
            // und der freigegebene Eintrag wartete eine Runde länger.
            entries = await listQueueEntries(uid).catch(() => entries);
        }

        for (const entry of entries) {
            switch (durchlaufEntscheid(entry, uid, nowIso, !!optionen?.abgewieseneErneut)) {
                // Bereits angehaltene Einträge (auch aus einer früheren
                // Sitzung) werden je Revision einmal gemeldet, nie hochgeladen.
                case 'melden':
                    announceTooOld(entry);
                    break;
                case 'anhalten':
                    await halteZuAltAn(uid, entry, nowIso);
                    break;
                case 'erneut': {
                    let erneut = false;
                    await mutateQueueEntry(uid, entry.projectId, e => {
                        const neu = erneutNurDiesenStand(e, entry);
                        erneut = !!neu;
                        return neu;
                    }).catch(() => {});
                    if (erneut) await flushProject(entry.projectId);
                    break;
                }
                case 'hochladen':
                    await flushProject(entry.projectId);
                    break;
                default:
                    break;
            }
        }
    }, [flushProject, announceTooOld, halteZuAltAn]);

    // Flush-Trigger: online, Tab wieder sichtbar, Sicherheitsintervall, Login.
    useEffect(() => {
        const onOnline = () => { void flushAll(); };
        const onVisible = () => { if (document.visibilityState === 'visible') void flushAll(); };
        window.addEventListener('online', onOnline);
        document.addEventListener('visibilitychange', onVisible);
        const interval = window.setInterval(() => {
            if (navigator.onLine && document.visibilityState === 'visible') void flushAll();
        }, 45_000);
        return () => {
            window.removeEventListener('online', onOnline);
            document.removeEventListener('visibilitychange', onVisible);
            window.clearInterval(interval);
        };
    }, [flushAll]);

    /**
     * Benutzerwechsel: die Gedächtnisse dieser Sitzung leeren.
     *
     * Beide merken sich „das haben wir schon gemeldet", und beide sind je
     * Projekt geführt — also über Benutzer hinweg verwechselbar. Nach einem
     * Wechsel am selben Gerät hielte der alte Inhalt Meldungen vom neuen
     * Anwender fern, die ihn sehr wohl angehen: seine eigene liegen
     * gebliebene Arbeit, und die Sperre, die ihm jemand gerade wegnimmt.
     *
     * Hier im Hook und nicht beim Aufrufer: die Refs gehören hierher, und der
     * Aufrufer soll von ihrer Existenz nichts wissen müssen.
     */
    useEffect(() => {
        announcedTooOldRef.current.clear();
        announcedHolderRef.current.clear();
    }, [userId]);

    useEffect(() => {
        if (userId) void flushAll({ abgewieseneErneut: true });
    }, [userId, flushAll]);

    // Sofort-Ausloeser der gebuendelten lokalen Sicherung: Seite verdeckt,
    // Seite verlassen, Fenster verliert den Fokus — und der Abbau dieses
    // Hooks (App ausgehaengt, StrictMode-Neumontage), nach dem kein
    // Zeitgeber des Buendels mehr jemanden haette, der auf ihn achtet. Der
    // Zeitgeber liefe zwar auf Modulebene weiter, aber was beim Abbau schon
    // im Speicher liegt, gehoert sofort auf die Platte. Keine
    // Abhaengigkeiten: das Buendel lebt im Modul, nicht in diesem Render.
    // Nur anstossen: ein Scheitern meldet `enqueue` ('storage-error'), der
    // Stand bleibt im Buendel und wird beim naechsten Anlass erneut versucht.
    useEffect(() => {
        const schreibeJetzt = () => { schreibeWartendeJetzt().catch(() => {}); };
        const abmelden = verdrahteSofortigeSicherung(window, document, schreibeJetzt);
        return () => {
            abmelden();
            schreibeJetzt();
        };
    }, []);

    /**
     * Haengt gerade etwas an der Warteschlange — eine lokale Sicherung oder
     * ein Hochladen?
     *
     * Gelesen vom erzwungenen Reload nach einem Sitzungsentzug
     * (sessionRevocation.ts) und von „Jetzt aktualisieren" (App.tsx), beide
     * ueber `warteAufRuhe`. Beides muss hinein: die IndexedDB-Transaktion
     * der lokalen Sicherung bricht beim Entladen ab, und ein Flush, der
     * zwischen `markSyncStart` und dem Aufraeumen abgeschnitten wird, laesst
     * den Eintrag fuer immer in 'syncing' liegen.
     *
     * „Lokale Sicherung" heisst seit dem 30.09.2026 auch: ein Stand, der im
     * Buendel noch auf seine 300 ms wartet — oder dessen Schreiben
     * gescheitert ist; der bleibt „beschaeftigt", bis ein neuer Versuch
     * glueckt. Und weil wer hier fragt gleich neu laden will, stoesst die
     * Frage das Schreiben SOFORT an (ein gescheitertes erneut) — sonst
     * wartete `warteAufRuhe` ohne Not bis zu 2 s auf den Zeitgeber.
     *
     * Die Flush-Reservierung faellt seit dem Umbau VOR den ersten `await` und
     * deckt damit auch den Schreibvorgang des Altersriegels (`markTooOld`) ab
     * — der lief vorher ausserhalb jeder Erfassung.
     */
    const istBeschaeftigt = useCallback(() => {
        const lokal = lokaleSicherungOffen();
        if (lokal) schreibeWartendeJetzt().catch(() => {});
        return lokal || flushingRef.current.size > 0;
    }, []);

    // Stabile Rueckgabe: App.tsx haengt Effekte und useCallback-Abhaengigkeiten
    // daran. Ein bei jedem Render frisch gebautes Objekt setzte dort Intervalle
    // und Kanaele in jedem Render neu auf.
    return useMemo(
        () => ({ enqueue, flushPending, flushProject, flushAll, istBeschaeftigt, discardTooOldEntry, acknowledgeTooOld, lokalenEntwurfHolen }),
        [enqueue, flushPending, flushProject, flushAll, istBeschaeftigt, discardTooOldEntry, acknowledgeTooOld, lokalenEntwurfHolen],
    );
};
